import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Grow from '@mui/material/Grow';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import ChecklistIcon from '@mui/icons-material/Checklist';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { refreshUnlockChains } from '../../../../actions/unlockChain';
import UnlockChainDialog from '../../../General/UnlockChainDialog';

const POLL_MS = 30000;

// What to do to put a step back, in the words the list shows.
const redoLabel = (step, t) => {
    if (step.kind === 'reverseTransfer') {
        return t('unlockChainRedoTransfer', { type: step.typeLabel, number: step.number });
    }
    if (step.kind === 'releaseReservation') {
        const s = step.snapshot || {};
        return t('unlockChainRedoReservation', { container: s.containerNumber, owner: `${s.ownerType || ''} ${s.ownerNumber || ''}`.trim() });
    }
    return t('unlockChainRedoSign', { type: step.typeLabel || '', number: step.number, status: step.originalStatusLabel || '' });
};

/**
 * App bar redo list for chain unlocks. Each chain lists what was walked back in the order it has
 * to be put back - the original record first, then downstream. The next actionable steps are
 * marked; signing a record ticks it off on the server, splits/combines and reservations are put
 * back from here, and anything can be ticked by hand.
 */
export default function UnlockChainList() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation('translation');
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [busyKey, setBusyKey] = useState(null);
    const [quantities, setQuantities] = useState({});
    const [resumeId, setResumeId] = useState(null);
    const chains = useSelector(state => state.unlockChain?.chains) || [];

    const refresh = useCallback(() => dispatch(refreshUnlockChains()), [dispatch]);

    useEffect(() => {
        refresh();
        const interval = setInterval(refresh, POLL_MS);
        return () => clearInterval(interval);
    }, [refresh]);

    const outstanding = chains.reduce((n, c) => n + c.steps.filter(s => s.state !== 'redone').length, 0);

    const act = useCallback(async (chain, step, action, body) => {
        setBusyKey(chain._id + step.key);
        try {
            await axios.post(`/api/unlockchain/${chain._id}/step/${encodeURIComponent(step.key)}/${action}`, body || {});
        } catch (err) {
            dispatch({ type: 'CREATE_ALERT', payload: { message: err.response?.data || err.message, title: t('error'), severity: 'error' } });
        } finally {
            setBusyKey(null);
            refresh();
        }
    }, [dispatch, refresh, t]);

    const abandon = useCallback(async (chain) => {
        try {
            await axios.post(`/api/unlockchain/${chain._id}/abandon`);
        } catch (err) {
            dispatch({ type: 'CREATE_ALERT', payload: { message: err.response?.data || err.message, title: t('error'), severity: 'error' } });
        } finally {
            refresh();
        }
    }, [dispatch, refresh, t]);

    if (chains.length === 0) return null;

    const stepIcon = (step) => {
        if (step.state === 'redone') return <CheckCircleIcon color="success" fontSize="small" />;
        if (step.state === 'pending') return <HourglassEmptyIcon color="disabled" fontSize="small" />;
        if (step.redoReady) return <PlayCircleOutlineIcon color="primary" fontSize="small" />;
        return <RadioButtonUncheckedIcon color="disabled" fontSize="small" />;
    };

    const stepActions = (chain, step) => {
        if (chain.status !== 'redoing' || step.state !== 'undone' || !step.redoReady) return null;
        const busy = busyKey === chain._id + step.key;
        if (step.kind === 'reverseTransfer') {
            const key = chain._id + step.key;
            const value = quantities[key] ?? step.snapshot?.quantity ?? '';
            return (
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <TextField size="small" type="number" value={value} label={t('quantity')} sx={{ width: 110 }}
                        onChange={e => setQuantities(q => ({ ...q, [key]: e.target.value }))} />
                    <Button size="small" variant="contained" disabled={busy}
                        onClick={() => act(chain, step, 'redo', { quantity: value })}>{t('unlockChainNext')}</Button>
                </Stack>
            );
        }
        const unrestored = step.kind === 'releaseReservation' ||
            (step.releasedReservations || []).some(r => !r.restored);
        return (
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                {unrestored &&
                    <Button size="small" variant="outlined" disabled={busy}
                        onClick={() => act(chain, step, 'redo')}>{t('unlockChainRestoreReservations')}</Button>
                }
                {step.kind === 'unlock' &&
                    <Button size="small" disabled={busy} onClick={() => act(chain, step, 'tick')}>{t('unlockChainTick')}</Button>
                }
            </Stack>
        );
    };

    return (
        <>
            <Tooltip title={t('unlockChainList')}>
                <IconButton ref={anchorRef} color="inherit" data-cy="unlockChainList"
                    onClick={() => { setOpen(o => !o); refresh(); }}>
                    <Badge overlap="rectangular" badgeContent={outstanding} color="secondary">
                        <ChecklistIcon />
                    </Badge>
                </IconButton>
            </Tooltip>
            <Popper open={open} anchorEl={anchorRef.current} role={undefined} transition disablePortal placement="bottom-end">
                {({ TransitionProps, placement }) => (
                    <Grow {...TransitionProps} sx={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}>
                        <Paper sx={{ width: 440, maxWidth: 'calc(100vw - 32px)' }}>
                            <ClickAwayListener onClickAway={() => setOpen(false)}>
                                <Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                                    <Typography variant="h6" sx={{ px: 1.5, pt: 1 }}>{t('unlockChainList')}</Typography>
                                    {chains.map(chain => {
                                        const redoOrder = [...chain.steps].sort((a, b) => b.seq - a.seq);
                                        return (
                                            <Box key={chain._id} sx={{ pb: 1 }}>
                                                <Divider />
                                                <Stack direction="row" sx={{ px: 1.5, pt: 1, alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Box>
                                                        <Typography variant="subtitle2">{chain.number} · {chain.root?.number}</Typography>
                                                        {chain.status === 'unwinding' &&
                                                            <Typography variant="caption" color="warning.main">{t('unlockChainStopped')}</Typography>
                                                        }
                                                    </Box>
                                                    <Stack direction="row" spacing={1}>
                                                        {chain.status === 'unwinding' &&
                                                            <Button size="small" variant="contained" onClick={() => { setResumeId(chain._id); setOpen(false); }}>
                                                                {t('unlockChainResume')}
                                                            </Button>
                                                        }
                                                        <Button size="small" color="inherit" onClick={() => abandon(chain)}>{t('unlockChainAbandon')}</Button>
                                                    </Stack>
                                                </Stack>
                                                <List dense disablePadding>
                                                    {redoOrder.map(step => (
                                                        <ListItem key={step.key} disablePadding sx={{ display: 'block' }}>
                                                            <ListItemButton disabled={step.url == null}
                                                                onClick={() => { navigate(step.url); setOpen(false); }}>
                                                                <ListItemIcon sx={{ minWidth: 32 }}>{stepIcon(step)}</ListItemIcon>
                                                                <ListItemText
                                                                    primary={redoLabel(step, t)}
                                                                    secondary={step.state === 'redone' ? t('unlockChainDone')
                                                                        : step.state === 'pending' ? t('unlockChainWaiting')
                                                                        : step.redoReady ? t('unlockChainNext') : t('unlockChainUndone')}
                                                                />
                                                            </ListItemButton>
                                                            <Box sx={{ pl: 6, pr: 1.5 }}>{stepActions(chain, step)}</Box>
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
            {resumeId != null &&
                <UnlockChainDialog show={true} chainId={resumeId} toggle={() => { setResumeId(null); refresh(); }} />
            }
        </>
    );
}
